import { useCallback, useMemo, useRef, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useWorkQueueContext } from '../context/useWorkQueueContext.js';
import { toastNotify } from '../utils/toastNotify.js';
import { taskActionErrorToast } from '../utils/taskActionToast.js';
import { currentUserUuid, toUiOwnerFromAssignee } from '../utils/taskOwner.js';
import { initialsFromName } from '../utils/initials.js';
import { reassignTask } from '../../../services/index.js';
import { UserCheck, Bell, UserCog } from 'lucide-react';

/**
 * Backend params for a task event, or `null` for non-task events. Only interim
 * `task` sources carry taskUuid/entityType/entityUuid, so only they persist via
 * the task service (CT-5093); the mock optimistic update still runs for any event.
 */
function taskBackendParams(event) {
  const detail = event?.detail ?? {};
  if (!detail.taskUuid || !detail.entityType || !detail.entityUuid) return null;
  return { taskUuid: detail.taskUuid, entityType: detail.entityType, entityUuid: detail.entityUuid };
}

/**
 * Team-level actions for My Team events: Take it (reassign to current user),
 * Nudge (ping the owner), Reassign (people picker). The optimistic owner update +
 * audit entry run for every event; for interim `task` events they also persist via
 * POST /v1/task/reassign/{task_uuid} (CT-5093), rolling back on failure. Nudge is
 * still mock-only (no endpoint — moved to a separate ticket).
 */
export function useTeamActions() {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const [reassignTarget, setReassignTarget] = useState(null);
  const { currentPersona, userNameById, updateEventOwner, appendAuditEntry } = useWorkQueueContext();
  /** Last owner confirmed by GS per event — used to roll back failed retries without undoing a prior success. */
  const lastPersistedOwnerRef = useRef(new Map());

  /** Roll the owner back, toast the reason, and report failure — shared by every persistReassign exit that can't persist. */
  const rollbackWithError = useCallback(
    (eventId, owner, errorLike) => {
      updateEventOwner(eventId, owner);
      const { title, message } = taskActionErrorToast(t, errorLike, 'reassign');
      toast.error({ title, message });
      return false;
    },
    [t, toast, updateEventOwner],
  );

  /**
   * Persist a reassign for a task event; roll the owner back + error toast on failure.
   * @returns {Promise<boolean>} `true` when the owner change should stick (incl. non-task / seed).
   */
  const persistReassign = useCallback(
    async (event, assigneeUuid, prevOwner, nextOwner) => {
      // Dev seeds are optimistic-only on purpose: their uuids are fake, so a real
      // call would 404. See constants/devSeedTasks.js.
      if (event.detail?.__seed) return true;
      const params = taskBackendParams(event);
      // A non-task event has no taskUuid/entityType/entityUuid, so `task/reassign`
      // cannot be called for it at all — and no governance endpoint has an assignee
      // to write to. This used to `return true`, which made the caller toast
      // "Reassigned to X" while nothing was sent anywhere: silent work loss.
      // Retrying will never help, so the copy says so instead of "reload and retry".
      if (!params) {
        return rollbackWithError(event.id, prevOwner, { code: 'TEAM_ACTION_UNSUPPORTED' });
      }
      // A real task we cannot persist (no assignee resolved, or already resolved)
      // must NOT report success — roll the owner back and surface the reason.
      if (!assigneeUuid || event.status === 'resolved') {
        return rollbackWithError(event.id, prevOwner, {
          code: event.status === 'resolved' ? 'TASK_NOT_OPEN' : 'TASK_IDS_MISSING',
        });
      }
      try {
        await reassignTask({ ...params, assigneeUuid });
        lastPersistedOwnerRef.current.set(event.id, nextOwner);
        return true;
      } catch (err) {
        const rollbackOwner = lastPersistedOwnerRef.current.get(event.id) ?? prevOwner;
        return rollbackWithError(event.id, rollbackOwner, err);
      }
    },
    [rollbackWithError],
  );

  const takeIt = useCallback(
    async (event) => {
      const prevOwner = event.owner;
      const me = currentUserUuid();
      // A real task with no viewer uuid can't be persisted — reporting a mock
      // owner change as success is silent work loss, so surface it as an error.
      const isTask = Boolean(taskBackendParams(event)) && !event.detail?.__seed;
      if (isTask && me == null) {
        const { title, message } = taskActionErrorToast(t, { code: 'TASK_IDS_MISSING' }, 'reassign');
        toast.error({ title, message });
        return;
      }
      const owner =
        me != null
          ? toUiOwnerFromAssignee(me, { nameById: userNameById, viewerUuid: me })
          : {
              id: currentPersona.id,
              name: currentPersona.name,
              initials: currentPersona.initials,
              isMine: true,
            };
      updateEventOwner(event.id, owner);
      // Always go through persistReassign — it is what decides whether the owner
      // change may stick. Skipping it when `me` is null reported success for events
      // that persist nothing.
      const ok = await persistReassign(event, me, prevOwner, owner);
      if (ok) {
        appendAuditEntry(event.id, {
          id: `team-take-${Date.now()}`,
          actor: currentPersona.name,
          action: t('team.auditTook'),
          timeAgo: t('team.justNow'),
        });
        toast.success({
          title: t('team.tookTitle'),
          message: t('team.tookMessage', { persona: owner.name }),
        });
      }
    },
    [appendAuditEntry, currentPersona, persistReassign, t, toast, updateEventOwner, userNameById],
  );

  const nudge = useCallback(
    (event) => {
      toastNotify(toast, {
        title: t('team.nudgedTitle'),
        message: t('team.nudgedMessage', { owner: event?.owner?.name ?? t('team.unassigned') }),
      });
    },
    [toast, t],
  );

  const openReassign = useCallback((event) => setReassignTarget(event), []);
  const closeReassign = useCallback(() => setReassignTarget(null), []);

  const reassign = useCallback(
    async (event, assignee) => {
      const prevOwner = event.owner;
      const me = currentUserUuid();
      const owner =
        toUiOwnerFromAssignee(assignee.id, {
          nameById: { ...userNameById, [assignee.id]: assignee.name },
          viewerUuid: me,
        }) ?? {
          id: assignee.id,
          name: assignee.name,
          initials: initialsFromName(assignee.name, assignee.id),
          isMine: me != null ? assignee.id === me : assignee.id === currentPersona.id,
        };
      updateEventOwner(event.id, owner);
      const ok = await persistReassign(event, assignee.id, prevOwner, owner);
      if (ok) {
        appendAuditEntry(event.id, {
          id: `team-reassign-${Date.now()}`,
          actor: currentPersona.name,
          action: t('team.auditReassigned', { assignee: assignee.name }),
          timeAgo: t('team.justNow'),
        });
        toast.success({
          title: t('team.reassignedTitle'),
          message: t('team.reassignedMessage', { assignee: assignee.name }),
        });
      }
      return ok;
    },
    [appendAuditEntry, currentPersona, persistReassign, t, toast, updateEventOwner, userNameById],
  );

  const buildMenuItems = useCallback(
    (event) => [
      { id: 'take-it', icon: UserCheck, label: t('team.takeIt'), onClick: () => takeIt(event) },
      { id: 'nudge', icon: Bell, label: t('team.nudge'), onClick: () => nudge(event) },
      { id: 'reassign', icon: UserCog, label: t('team.reassign'), onClick: () => openReassign(event) },
    ],
    [t, takeIt, nudge, openReassign],
  );

  return useMemo(
    () => ({ takeIt, nudge, reassign, openReassign, closeReassign, reassignTarget, buildMenuItems }),
    [takeIt, nudge, reassign, openReassign, closeReassign, reassignTarget, buildMenuItems],
  );
}
